import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchStudentResponses,
  fetchStudentResponseFilterOptions,
  reevaluateResponse,
  reevaluateAllResponses,
  setResponseVerdict,
} from "../pages/services/studentResponseService";
import EditQuestionModal from "./EditQuestionModal";
import DropDownComponent from "./formComponents/DropDownComponent";
import StudentResponsesSummary from "./StudentResponsesSummary";
import "../styles/StudentResponses.css";
import { renderContentHtml } from "./utils/richText";
import { useHighlightCode } from "./utils/utility";

const LIMIT = 10;

const RESULT_OPTIONS = [
  { label: "All", value: "" },
  { label: "Correct", value: "correct" },
  { label: "Incorrect", value: "incorrect" },
];

const DEFAULT_FILTERS = {
  responseType: "",
  studentId: "",
  subjectId: "",
  batchId: "",
  result: "",
  fromDate: "",
  toDate: "",
};


// Where a question was answered. Practice/example/assignment/quiz come from the
// section it lives in; a test carries its own title so two tests are told apart.
const SECTION_TAG_LABELS = {
  practice: "Practice question",
  example: "Example question",
  assignment: "Assignment question",
  quiz: "Quiz question",
};

const ResponseTag = ({ row }) => {
  if (row.sectionType === "test") {
    return (
      <span className="sr-tag sr-tag--test" title="Answered as part of a test">
        Test - {row.testTitle || "Untitled test"}
      </span>
    );
  }
  const label = SECTION_TAG_LABELS[row.sectionType];
  if (!label) return null;
  return <span className={`sr-tag sr-tag--${row.sectionType}`}>{label}</span>;
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function stripHtmlPreview(html, maxLen = 140) {
  const text = String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > maxLen ? text.slice(0, maxLen) + "…" : text;
}

// ─── Full, formatted view of one response ────────────────────────────────────
// The table shows one-line previews so it stays scannable; expanding a row
// shows the question, both answers and the solution as they were written.
// Admin-authored content renders as rich text (lists, tables, images, code);
// content with no markup is plain text and keeps its line breaks and
// indentation. What a student typed is never treated as markup.
const hasMarkup = (s) => /<[a-zA-Z][^>]*>/.test(String(s || ""));

const RichBlock = ({ value, empty = "—" }) => {
  const text = String(value ?? "");
  if (!text.trim()) return <div className="sr-detail-box sr-muted">{empty}</div>;
  return hasMarkup(text)
    ? <div className="sr-detail-box sr-rich" dangerouslySetInnerHTML={{ __html: renderContentHtml(text) }} />
    : <div className="sr-detail-box sr-rich sr-plain">{text}</div>;
};

const ResponseDetail = ({ row }) => {
  const isCode = /code/i.test(row.questionType || "");
  return (
    <div className="sr-detail">
      <div className="sr-detail-label">Question</div>
      <RichBlock value={row.questionText} empty="Question deleted" />
      <div className="sr-detail-grid">
        <div>
          <div className="sr-detail-label">Submitted answer</div>
          {row.isOptionSelectAnswer
            ? <RichBlock value={row.submittedAnswerText} empty="Not attempted" />
            : row.submittedAnswerText
              ? <div className={`sr-detail-box sr-plain${isCode ? " sr-mono" : ""}`}>{row.submittedAnswerText}</div>
              : <div className="sr-detail-box sr-muted">Not attempted</div>}
        </div>
        <div>
          <div className="sr-detail-label">Correct answer</div>
          <RichBlock value={row.correctAnswerText} />
        </div>
      </div>
      {row.answerExplanation && (
        <>
          <div className="sr-detail-label">Solution</div>
          <RichBlock value={row.answerExplanation} />
        </>
      )}
    </div>
  );
};

const ResultBadge = ({ row }) => {
  if (row.reviewStatus === "pending") {
    return <span className="sr-badge sr-badge-pending" title="Waiting for the trainer's review">Needs review</span>;
  }
  const auto = row.reviewStatus === "autoGraded";
  return (
    <span className={`sr-badge ${row.correct ? "sr-badge-correct" : "sr-badge-incorrect"}`}
          title={auto ? `Graded automatically${row.autoGrade?.label ? ` — ${row.autoGrade.label}` : ""}` : undefined}>
      {row.correct ? "Correct" : "Incorrect"}{auto ? " · auto" : ""}
    </span>
  );
};

const StudentResponses = () => {
  const [expanded, setExpanded] = useState(() => new Set());
  const tableRef = useRef(null);
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [reevaluatingAll, setReevaluatingAll] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [reevaluatingId, setReevaluatingId] = useState(null);
  // Change verdict: the row whose Correct / Incorrect choice is open, and the
  // one being saved.
  const [verdictOpenId, setVerdictOpenId] = useState(null);
  const [savingVerdictId, setSavingVerdictId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState({ students: [], subjects: [], batches: [], responseTypes: [] });

  useEffect(() => {
    fetchStudentResponseFilterOptions()
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  const loadPage = useCallback(async (pageNum, activeFilters) => {
    try {
      const data = await fetchStudentResponses(pageNum, LIMIT, activeFilters);
      setResponses((prev) => (pageNum === 1 ? data.responses : [...prev, ...data.responses]));
      setTotal(data.total);
      setPage(pageNum);
    } catch {
      setError("Could not load student responses.");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadPage(1, filters).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await loadPage(page + 1, filters);
    setLoadingMore(false);
  };

  const handleLoadAll = async () => {
    setLoadingAll(true);
    try {
      const data = await fetchStudentResponses(1, Math.max(total, LIMIT), filters);
      setResponses(data.responses);
      setTotal(data.total);
      setPage(1);
    } catch {
      setError("Could not load all student responses.");
    } finally {
      setLoadingAll(false);
    }
  };

  const handleReevaluateAll = async () => {
    const scopeLabel = hasActiveFilters ? `the ${total} filtered responses` : `all ${total} responses`;
    if (!window.confirm(`Re-evaluate ${scopeLabel}? This may take a moment.`)) return;

    setReevaluatingAll(true);
    setError("");
    setInfoMessage("");
    try {
      const result = await reevaluateAllResponses(filters);
      setInfoMessage(`Re-evaluated ${result.totalMatched} responses — ${result.totalChanged} results changed.`);
      await loadPage(1, filters);
    } catch {
      setError("Could not re-evaluate responses.");
    } finally {
      setReevaluatingAll(false);
    }
  };

  // Any answered response — descriptive, auto-graded or MCQ — can be set
  // correct or incorrect by hand. It is then recorded as a review by Admin
  // (the student is told), and Re-evaluate leaves it alone.
  const handleSetVerdict = async (row, verdict) => {
    setSavingVerdictId(row._id);
    setError("");
    try {
      const result = await setResponseVerdict(row._id, verdict);
      setResponses((prev) =>
        prev.map((r) =>
          r._id === row._id
            ? { ...r, correct: result.correct, reviewStatus: result.reviewStatus,
                reviewedAt: result.reviewedAt, reviewedByName: result.reviewedByName }
            : r
        )
      );
      setVerdictOpenId(null);
      setInfoMessage(verdict === "correct" ? "Marked correct." : "Marked incorrect.");
    } catch (e) {
      setError(e.message || "Could not change the verdict.");
    } finally {
      setSavingVerdictId(null);
    }
  };

  const handleReevaluate = async (row) => {
    setReevaluatingId(row._id);
    try {
      const result = await reevaluateResponse(row._id);
      setResponses((prev) =>
        prev.map((r) =>
          r._id === row._id
            ? { ...r, correct: result.correct, correctAnswerText: result.correctAnswerText }
            : r
        )
      );
    } catch (e) {
      setError(e.message || "Could not re-evaluate this response.");
    } finally {
      setReevaluatingId(null);
    }
  };

  const handleEditClick = (row) => {
    // Only redirect when the correct answer is itself an option id chosen by
    // clicking (quiz, or MCQ in "example") — there, editing options requires
    // the full question editor. A practice/assignment MCQ question is still
    // isMCQ but was answered as free text; EditQuestionModal handles that
    // case safely (its Answer field is disabled for MCQ questions).
    if (row.isOptionSelectAnswer) {
      window.open(`/questions/${row.questionId}/update`, "_blank", "noopener,noreferrer");
      return;
    }
    setEditRow(row);
  };

  const handleQuestionSaved = ({ questionId, question, answer, answerExplanation }) => {
    setResponses((prev) =>
      prev.map((r) =>
        r.questionId === questionId
          ? {
              ...r,
              questionText: question,
              // Kept as written: the table previews it, the expanded row renders it.
              correctAnswerText: r.isMCQ ? r.correctAnswerText : String(answer || ""),
              answerExplanation,
            }
          : r
      )
    );
  };

  const toggleRow = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allExpanded = responses.length > 0 && responses.every((r) => expanded.has(r._id));
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(responses.map((r) => r._id)));
  // Colour code blocks in whatever has just been expanded.
  useHighlightCode(tableRef, [expanded, responses]);

  const hasMore = responses.length < total;
  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="sr-container">
      <div className="sr-header">
        <h2>Student Responses</h2>
        <div className="sr-header-actions">
          <span className="sr-total">{total} total submissions</span>
          <button
            className="sr-header-btn"
            onClick={handleLoadAll}
            disabled={loadingAll || responses.length >= total || total === 0}
          >
            {loadingAll ? "Loading all…" : "Load All"}
          </button>
          <button
            className="sr-header-btn sr-header-btn-primary"
            onClick={handleReevaluateAll}
            disabled={reevaluatingAll || total === 0}
          >
            {reevaluatingAll ? "Re-evaluating…" : "Re-evaluate All"}
          </button>
        </div>
      </div>

      {infoMessage && <div className="sr-info">{infoMessage}</div>}

      <div className="sr-toolbar">
        <DropDownComponent
          options={filterOptions.responseTypes || []}
          value={filters.responseType}
          onChange={(v) => handleFilterChange("responseType", v)}
          placeholder="Question type"
        />
        <DropDownComponent
          options={filterOptions.students || []}
          value={filters.studentId}
          onChange={(val) => handleFilterChange("studentId", val)}
          placeholder="Student"
        />
        <DropDownComponent
          options={filterOptions.subjects || []}
          value={filters.subjectId}
          onChange={(val) => handleFilterChange("subjectId", val)}
          placeholder="Subject"
        />
        <DropDownComponent
          options={RESULT_OPTIONS}
          value={filters.result}
          onChange={(val) => handleFilterChange("result", val)}
          placeholder="Result"
        />
        <DropDownComponent
          options={filterOptions.batches || []}
          value={filters.batchId}
          onChange={(val) => handleFilterChange("batchId", val)}
          placeholder="Batch"
        />
        <div className="sr-date-range">
          <label>
            From
            <input
              type="datetime-local"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="datetime-local"
              value={filters.toDate}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
            />
          </label>
        </div>
        {hasActiveFilters && (
          <button className="sr-clear-filters-btn" onClick={() => setFilters(DEFAULT_FILTERS)}>
            Clear filters
          </button>
        )}
      </div>

      {error && <div className="sr-error">{error}</div>}

      {/* Summary of exactly the rows the filters select. */}
      <StudentResponsesSummary filters={filters} />


      {loading ? (
        <div className="sr-loading">
          <div className="sr-spinner" />
          <p>Loading responses…</p>
        </div>
      ) : responses.length === 0 ? (
        <div className="sr-empty">
          {hasActiveFilters ? "No responses match these filters." : "No student responses yet."}
        </div>
      ) : (
        <>
          <div className="sr-table-tools">
            <button className="sr-link-btn" onClick={toggleAll}>
              {allExpanded ? "Collapse all" : "Expand all"}
            </button>
          </div>
          <div className="sr-table-wrap" ref={tableRef}>
            <table className="sr-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Student</th>
                  <th>Submitted Answer</th>
                  <th>Result</th>
                  <th>Correct Answer</th>
                  <th>Submitted At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {responses.map((row) => (
                  <React.Fragment key={row._id}>
                  <tr className={expanded.has(row._id) ? "sr-row-open" : ""}>
                    <td className="sr-question-cell">
                      <button
                        type="button"
                        className="sr-expand-btn"
                        aria-expanded={expanded.has(row._id)}
                        onClick={() => toggleRow(row._id)}
                        title={expanded.has(row._id) ? "Hide details" : "Show the full question, answers and solution"}
                      >
                        <span className="sr-chevron" aria-hidden="true">{expanded.has(row._id) ? "▾" : "▸"}</span>
                        <span>
                          {row.questionExists ? stripHtmlPreview(row.questionText) : (
                            <span className="sr-deleted">Question deleted</span>
                          )}
                        </span>
                      </button>
                      <ResponseTag row={row} />
                    </td>
                    <td>{row.studentName}</td>
                    <td>{stripHtmlPreview(row.submittedAnswerText) || <span className="sr-muted">—</span>}</td>
                    <td><ResultBadge row={row} /></td>
                    <td>{stripHtmlPreview(row.correctAnswerText) || <span className="sr-muted">—</span>}</td>
                    <td>{fmtDateTime(row.answeredAt)}</td>
                    <td className="sr-actions-cell">
                      <button
                        className="sr-action-btn"
                        disabled={!row.questionExists}
                        onClick={() => handleEditClick(row)}
                      >
                        Edit Question
                      </button>
                      <button
                        className="sr-action-btn"
                        disabled={!row.questionExists || reevaluatingId === row._id}
                        onClick={() => handleReevaluate(row)}
                      >
                        {reevaluatingId === row._id ? "Re-evaluating…" : "Re-evaluate"}
                      </button>
                      {String(row.submittedAnswerText || "").trim() && (
                        verdictOpenId === row._id ? (
                          <span className="sr-verdict-choice">
                            <button
                              className="sr-action-btn sr-action-btn-correct"
                              disabled={savingVerdictId === row._id || (row.correct === true && row.reviewStatus !== "pending")}
                              onClick={() => handleSetVerdict(row, "correct")}
                            >
                              ✓ Correct
                            </button>
                            <button
                              className="sr-action-btn sr-action-btn-incorrect"
                              disabled={savingVerdictId === row._id || (row.correct === false && row.reviewStatus !== "pending")}
                              onClick={() => handleSetVerdict(row, "incorrect")}
                            >
                              ✗ Incorrect
                            </button>
                            <button
                              className="sr-action-btn"
                              disabled={savingVerdictId === row._id}
                              onClick={() => setVerdictOpenId(null)}
                              aria-label="Cancel"
                            >
                              {savingVerdictId === row._id ? "Saving…" : "Cancel"}
                            </button>
                          </span>
                        ) : (
                          <button className="sr-action-btn" onClick={() => setVerdictOpenId(row._id)}>
                            {row.reviewStatus === "pending" ? "Give verdict" : "Change verdict"}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                  {expanded.has(row._id) && (
                    <tr className="sr-detail-row">
                      <td colSpan={7}><ResponseDetail row={row} /></td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="sr-load-more-row">
              <button className="sr-load-more-btn" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : `Load more (${total - responses.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}

      <EditQuestionModal
        isOpen={!!editRow}
        onClose={() => setEditRow(null)}
        response={editRow}
        onSaved={handleQuestionSaved}
      />
    </div>
  );
};

export default StudentResponses;
